import { Component, OnInit, signal, inject } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { CheckboxModule } from 'primeng/checkbox';
import { MessageService } from 'primeng/api';
import { AuthService } from './core/auth/auth.service';
import { ZustimmungService } from './core/services/zustimmung.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    ButtonModule,
    ToastModule,
    DialogModule,
    CheckboxModule
  ],
  providers: [MessageService],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit {
  private zustimmungService = inject(ZustimmungService);
  private messageService = inject(MessageService);

  haendlerBezeichnung = '';
  haendlerBezeichnungShort = '';
  haendlerId = 0;
  userName = '';
  menuOpen = false;

  zustimmungPending = signal(false);
  zustimmungAkzeptiert = false;
  zustimmungSaving = signal(false);

  constructor(private authService: AuthService, private router: Router) {}

  ngOnInit(): void {
    this.haendlerBezeichnung = this.authService.getHaendlerBezeichnung();
    this.haendlerBezeichnungShort = this.haendlerBezeichnung.length > 30
      ? this.haendlerBezeichnung.slice(0, 30) + '…'
      : this.haendlerBezeichnung;
    this.haendlerId = this.authService.getHaendlerId();
    this.userName = this.authService.getUserName();
    this.checkZustimmung();
    if (this.authService.isImpersonated()) {
      setTimeout(() => {
        this.messageService.add({
          severity: 'info',
          summary: 'Labs Projekt per Impersonate geöffnet.',
          detail: 'Dein Nutzungsverhalten innerhalb der Anwendung wird nicht protokolliert, um die Nutzungsstatistiken nicht zu verfälschen. Die Prüfung auf die Aktivierung des Features für den Händler wurde aufgrund des Impersonate übersprungen.',
          life: 10000,
          styleClass: 'feedback-toast'
        });
      }, 0);
    }
  }

  private async checkZustimmung(): Promise<void> {
    if (this.authService.isImpersonated()) return;
    if (!this.authService.hasRole('feat-labs-abholfachzettel-express')) return;
    try {
      const status = await this.zustimmungService.getStatus();
      if (!status.zugestimmt) {
        this.zustimmungPending.set(true);
      }
    } catch {
      // Fail open: API nicht erreichbar → kein Modal
    }
  }

  async onZustimmen(): Promise<void> {
    this.zustimmungSaving.set(true);
    try {
      await this.zustimmungService.save();
      this.zustimmungPending.set(false);
      this.messageService.add({
        severity: 'success',
        icon: 'pi-heart',
        summary: 'Wie schön, dass du da bist!',
        detail: 'Los gehts! Viel Spaß beim Erkunden dieses Quimus Labs Features.',
        life: 8000,
        styleClass: 'feedback-toast'
      });
    } catch {
      // Fehler still ignorieren – Button bleibt aktiv für erneuten Versuch
    } finally {
      this.zustimmungSaving.set(false);
    }
  }

  navigateTo(path: string): void {
    this.router.navigate([path]);
  }

  logout(): void {
    this.authService.logout();
  }

  toggleMenu(): void {
    this.menuOpen = !this.menuOpen;
  }

  closeMenu(): void {
    this.menuOpen = false;
  }
}
